import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  IoArrowForward,
  IoBarChartOutline,
  IoChevronForward,
  IoCloudOfflineOutline,
  IoCubeOutline,
  IoDocumentTextOutline,
  IoFlameOutline,
  IoFolderOpenOutline,
  IoPhonePortraitOutline,
  IoPlayCircleOutline,
} from "react-icons/io5";
import { useEffectiveAudience } from "../../context/AudienceContext";
import { ProjectSection, asIcon } from "../../components/Landing/ProjectSection";
import { ApproachSection } from "../../components/Landing/ApproachSection";
import { PerspectiveToggle } from "../../components/PerspectiveToggle";
import { useReveal } from "../../hooks/useReveal";
import styles from "./landing.module.css";

const YOUTUBE_VIDEO_ID = "hPBnEnA06uc";
const YOUTUBE_WATCH_URL = `https://www.youtube.com/watch?v=${YOUTUBE_VIDEO_ID}`;
const YOUTUBE_EMBED_URL = `https://www.youtube-nocookie.com/embed/${YOUTUBE_VIDEO_ID}?rel=0`;

type Lens = "patient" | "provider";

type HeroSpec = {
  label: string;
  /** Headline split so the second half can carry the brand highlight. */
  title: [string, string];
  lead: string;
  cta: { to: string; label: string };
  secondary: { href: string; label: string; internal?: boolean };
  image: string;
  imageAlt: string;
};

type StepItem = {
  title: string;
  body: string;
};

type AudienceRow = {
  to: string;
  title: string;
  body: string;
  action: string;
};

type ActivityGroup = {
  name: string;
  summary: string;
  activities: readonly string[];
};

const HERO: Record<Lens, HeroSpec> = {
  patient: {
    label: "Guided stroke recovery",
    title: ["Your phone is now", "your rehab coach."],
    lead:
      "Slip your phone into a 3D-printed mug or bowl, and every lift, twist and pour is tracked with instant feedback.",
    cta: { to: "/shopping", label: "Get the kit" },
    secondary: { href: "#demo", label: "Watch the demo" },
    image: "/images/product/mug-and-bowl-still-image.jpeg",
    imageAlt:
      "The mRehab kit: a branded box, a copper 3D-printed mug holding a phone, and a matching bowl on a mat",
  },
  provider: {
    label: "For care teams",
    title: ["See every rep", "your patients do at home."],
    lead:
      "Assign guided home activities, then review sessions and trends in one dashboard, under your own protocols.",
    cta: { to: "/how-it-works", label: "How it works" },
    secondary: { href: "/research", label: "See the evidence", internal: true },
    image: "/images/product/mug-and-bowl-still-image.jpeg",
    imageAlt:
      "The mRehab kit: a branded box, a copper 3D-printed mug holding a phone, and a matching bowl on a mat",
  },
};

const ACTIVITY_GROUPS: readonly ActivityGroup[] = [
  {
    name: "Movement",
    summary: "Lift, reach and slide with control.",
    activities: [
      "Vertical Mug",
      "Horizontal Mug",
      "Vertical Bowl",
      "Horizontal Bowl",
    ],
  },
  {
    name: "Rotation",
    summary: "Turn your wrist like you would with a jar or a jug.",
    activities: ["Sip from Mug", "Quick Twist", "Slow Pour"],
  },
  {
    name: "Function",
    summary: "Fine finger work for the small things.",
    activities: ["Phone Number", "Quick Tap"],
  },
];

const HOW_INTRO: Record<Lens, string> = {
  patient: "From the box to your first session in minutes.",
  provider: "From assigning work to closing the loop.",
};

const HOW_ITEMS: Record<Lens, readonly StepItem[]> = {
  patient: [
    {
      title: "Unbox and set up",
      body: "Install the free app, slide your phone into the mug or bowl, and you are ready.",
    },
    {
      title: "Follow along",
      body: "Watch the demo, then go. Your phone counts every rep and gives feedback as you move.",
    },
    {
      title: "Watch yourself improve",
      body: "Streaks, goals and session history show your progress. Share it with your therapist anytime.",
    },
  ],
  provider: [
    {
      title: "Align assignments",
      body: "Recommend activities that match each patient's plan and upper-limb focus areas.",
    },
    {
      title: "Monitor engagement",
      body: "Scan session summaries between visits to see who is practicing and who needs a nudge.",
    },
    {
      title: "Document and export",
      body: "Use exports and in-app views according to your institution's documentation standards.",
    },
  ],
};

const AUDIENCE_ROWS: Record<Lens, AudienceRow> = {
  patient: {
    to: "/shopping",
    title: "Survivors and families",
    body: "Rebuild reach, grip and everyday confidence with guided practice between visits.",
    action: "Get the kit",
  },
  provider: {
    to: "/how-it-works",
    title: "Clinicians and programs",
    body: "Adherence and movement summaries between visits, subject to your protocols and consent.",
    action: "How it works",
  },
};

const AUDIENCE_ICONS: Record<Lens, React.ElementType> = {
  patient: asIcon(IoFolderOpenOutline),
  provider: asIcon(IoDocumentTextOutline),
};

const ArrowIcon = asIcon(IoArrowForward);
const ChevronIcon = asIcon(IoChevronForward);
const PlayIcon = asIcon(IoPlayCircleOutline);
const PhoneIcon = asIcon(IoPhonePortraitOutline);
const CubeIcon = asIcon(IoCubeOutline);
const ChartIcon = asIcon(IoBarChartOutline);
const FlameIcon = asIcon(IoFlameOutline);
const OfflineIcon = asIcon(IoCloudOfflineOutline);

/** Heading block shared by the open (unboxed) sections. */
function SectionHead({
  titleId,
  title,
  lead,
}: {
  titleId: string;
  title: string;
  lead?: string;
}) {
  return (
    <div className={styles.sectionHead}>
      <h2 id={titleId} className={styles.sectionTitle}>
        {title}
      </h2>
      {lead ? <p className={styles.sectionLead}>{lead}</p> : null}
    </div>
  );
}

/** Hero: oversized promise on the brand panel, kit photo bleeding off the edge. */
function HeroPanel({ lens }: { lens: Lens }) {
  const hero = HERO[lens];
  const secondaryClass = `ui-btn ui-btn--lg ${styles.heroGhost}`;
  return (
    <section className={styles.hero} aria-labelledby="landing-hero-title">
      <div className={styles.heroCopy}>
        <span className={styles.heroLabel}>{hero.label}</span>
        <h1 id="landing-hero-title" className={styles.heroTitle}>
          {hero.title[0]}{" "}
          <span className={styles.heroAccent}>{hero.title[1]}</span>
        </h1>
        <p className={styles.heroLead}>{hero.lead}</p>
        <div className={styles.heroActions}>
          <Link
            to={hero.cta.to}
            className={`ui-btn ui-btn--lg ui-btn--inverted ${styles.heroCta}`}
          >
            {hero.cta.label}
            <ArrowIcon aria-hidden />
          </Link>
          {hero.secondary.internal ? (
            <Link to={hero.secondary.href} className={secondaryClass}>
              {hero.secondary.label}
            </Link>
          ) : (
            <a href={hero.secondary.href} className={secondaryClass}>
              <PlayIcon aria-hidden />
              {hero.secondary.label}
            </a>
          )}
        </div>
      </div>
      <div className={styles.heroMedia}>
        <img
          src={hero.image}
          alt={hero.imageAlt}
          loading="eager"
          fetchPriority="high"
        />
      </div>
    </section>
  );
}

/** The four pillars from the flyer, as an uneven bento. */
function KitBento() {
  const ref = useReveal<HTMLElement>();
  return (
    <section className="ui-reveal" aria-labelledby="landing-kit-title" ref={ref}>
      <SectionHead
        titleId="landing-kit-title"
        title="One kit. Everything you need to keep moving."
        lead="A smart app, tools you can hold, and a clear picture of how far you've come."
      />
      <div className={styles.bento}>
        <article className={`${styles.cell} ${styles.cellApp}`}>
          <img
            src="/images/product/water-pour-activity-new.png"
            alt="A hand tilting the copper mug with a phone inside, the app counting the rep on screen"
            loading="lazy"
            decoding="async"
          />
          <div className={styles.cellAppText}>
            <PhoneIcon aria-hidden className={styles.cellIcon} />
            <h3>mRehab recognizes your movements</h3>
            <p>
              Your phone's motion sensors track each movement and give you
              instant feedback.
            </p>
          </div>
        </article>

        <article className={`${styles.cell} ${styles.cellCount}`}>
          <span className={styles.bigNum} aria-hidden>
            9
          </span>
          <h3>Guided Activities</h3>
          <p>
            Built from everyday habits like sipping, pouring and dialing a
            number.
          </p>
          <a href="#activities" className={styles.cellLink}>
            See all nine <ArrowIcon aria-hidden />
          </a>
        </article>

        <article className={`${styles.cell} ${styles.cellTools}`}>
          <div className={styles.cellToolsMedia}>
            <img
              src="/images/product/mug-and-bowl-3d-printer.png"
              alt="The copper 3D-printed mug and bowl on a workbench in front of a 3D printer"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className={styles.cellBody}>
            <CubeIcon aria-hidden className={styles.cellIcon} />
            <h3>3D-printed tools</h3>
            <p>
              A low-cost mug and bowl that feel like the real thing, because
              practice should look like life.
            </p>
          </div>
        </article>

        <article className={`${styles.cell} ${styles.cellProgress}`}>
          <div className={styles.cellProgressText}>
            <ChartIcon aria-hidden className={styles.cellIcon} />
            <h3>Progress you can see</h3>
            <p>
              Every exercise and every session, tracked in detail. Small wins
              add up, and now you can watch them.
            </p>
            <ul className={styles.progressPoints}>
              <li>
                <FlameIcon aria-hidden /> Keep streaks, set goals
              </li>
              <li>
                <OfflineIcon aria-hidden /> Works offline, syncs later
              </li>
            </ul>
          </div>
          <div className={styles.cellProgressMedia}>
            <img
              src="/images/product/vertical-mug-activity-new-2.png"
              alt="A man seated at a table raising the copper mug overhead during a vertical mug activity"
              loading="lazy"
              decoding="async"
            />
          </div>
        </article>
      </div>
    </section>
  );
}

/** All nine activities, grouped the way the flyer groups them. */
function ActivitiesSection() {
  const ref = useReveal<HTMLElement>();
  return (
    <section
      className={`ui-reveal ${styles.activities}`}
      id="activities"
      aria-labelledby="landing-activities-title"
      ref={ref}
    >
      <div className={styles.activitiesHead}>
        <span className={styles.activitiesNum} aria-hidden>
          9
        </span>
        <h2 id="landing-activities-title" className={styles.sectionTitle}>
          Nine activities that mimic real life.
        </h2>
        <p className={styles.sectionLead}>
          Each one trains a motion you use every day, so progress shows up
          at the kitchen table, not just on a chart.
        </p>
      </div>
      <div className={styles.groups}>
        {ACTIVITY_GROUPS.map((group) => (
          <div key={group.name} className={styles.group}>
            <div className={styles.groupHead}>
              <h3 className={styles.groupName}>{group.name}</h3>
              <p className={styles.groupSummary}>{group.summary}</p>
            </div>
            <ul className={styles.chips}>
              {group.activities.map((activity) => (
                <li key={activity} className={styles.chip}>
                  {activity}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

/** How you use it: three steps threaded on a single rule. */
function HowSection({ lens }: { lens: Lens }) {
  const ref = useReveal<HTMLElement>();
  return (
    <section className="ui-reveal" aria-labelledby="landing-how-title" ref={ref}>
      <SectionHead
        titleId="landing-how-title"
        title={lens === "patient" ? "3 easy steps." : "Three steps for your team."}
        lead={HOW_INTRO[lens]}
      />
      <ol className={styles.steps}>
        {HOW_ITEMS[lens].map((step, i) => (
          <li key={step.title} className={styles.step}>
            <span className={styles.stepNum} aria-hidden>
              {i + 1}
            </span>
            <h3 className={styles.stepTitle}>{step.title}</h3>
            <p className={styles.stepBody}>{step.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function VideoCard() {
  const ref = useReveal<HTMLElement>();
  return (
    <section
      className={`ui-card ui-card--lg ui-reveal ${styles.video}`}
      id="demo"
      aria-labelledby="landing-video-title"
      ref={ref}
    >
      <div className="ui-card-header">
        <PlayIcon aria-hidden />
        <div className="ui-card-header-text">
          <h2 id="landing-video-title">Watch mRehab in action</h2>
          <p>The kit, the app, and a real session, start to finish.</p>
        </div>
      </div>
      <div className="ui-card-body">
        <div className="ui-media ui-media--video">
          <iframe
            src={YOUTUBE_EMBED_URL}
            title="mRehab product demo on YouTube"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
        <p className="ui-caption">
          Prefer YouTube?{" "}
          <a
            className="ui-link"
            href={YOUTUBE_WATCH_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open this video in a new tab
          </a>
          .
        </p>
      </div>
    </section>
  );
}

/** Who it's for: two highlight rows, with no card wrapped around them. */
function AudienceSection({ lens }: { lens: Lens }) {
  const ref = useReveal<HTMLElement>();
  const ordered: readonly Lens[] =
    lens === "provider" ? ["provider", "patient"] : ["patient", "provider"];

  return (
    <section
      className="ui-reveal"
      aria-labelledby="landing-audience-title"
      ref={ref}
    >
      <SectionHead titleId="landing-audience-title" title="Made for both sides of recovery" />
      <div className={styles.audienceRows}>
        {ordered.map((key) => {
          const row = AUDIENCE_ROWS[key];
          const Icon = AUDIENCE_ICONS[key];
          return (
            <Link key={key} to={row.to} className="ui-highlight-row">
              <span className="ui-icon-chip">
                <Icon aria-hidden />
              </span>
              <span className="ui-list-text">
                <span className="ui-list-label">{row.title}</span>
                <span className="ui-list-desc">{row.body}</span>
              </span>
              <span className="ui-list-trailing">
                <span className={styles.rowAction}>{row.action}</span>
                <ChevronIcon aria-hidden />
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

/** Closing call to action, echoing the flyer's "Try now". */
function ClosingCta() {
  const ref = useReveal<HTMLElement>();
  return (
    <section
      className={`ui-reveal ${styles.closing}`}
      aria-labelledby="landing-closing-title"
      ref={ref}
    >
      <div className={styles.closingCopy}>
        <h2 id="landing-closing-title" className={styles.closingTitle}>
          Your next rep starts at home.
        </h2>
        <p className={styles.closingLead}>
          Download the free app and order a kit. Questions first? We're happy
          to talk.
        </p>
      </div>
      <div className={styles.closingActions}>
        <Link to="/shopping" className="ui-btn ui-btn--lg ui-btn--inverted">
          Get the kit
          <ArrowIcon aria-hidden />
        </Link>
        <Link to="/contact" className={`ui-btn ui-btn--lg ${styles.heroGhost}`}>
          Contact us
        </Link>
      </div>
    </section>
  );
}

const Landing: React.FC = () => {
  const effective = useEffectiveAudience();
  const [chosenLens, setChosenLens] = useState<Lens>("patient");

  const both = effective === "both";
  const lens: Lens = both ? chosenLens : effective;

  return (
    <main id="main" className={`ui-page ${styles.page}`}>
      <div className={`ui-container ${styles.heroBlock}`}>
        {both ? (
          <div className={styles.toggleRow}>
            <PerspectiveToggle value={chosenLens} onChange={setChosenLens} />
          </div>
        ) : null}
        <HeroPanel lens={lens} />
      </div>


      <div className={`ui-container ${styles.block}`}>
        <KitBento />
        <ActivitiesSection />
      </div>

      <ApproachSection />

      <div className={`ui-container ${styles.block}`}>
        <HowSection lens={lens} />
        <VideoCard />
      </div>

      <ProjectSection />

      <div className={`ui-container ${styles.block}`}>
        <AudienceSection lens={lens} />
        <ClosingCta />
      </div>
    </main>
  );
};

export default Landing;
